// Folding by scope, and selecting by scope: the engine's scopes (named
// definitions) as the units the reader folds to one line and grows a line
// selection to. Scopes are the new side's; a fold hides the rows after the
// scope's head line through its last line, whichever blocks hold them, and
// the removed rows among them.
import type { Row, Scope } from '../engine/types';
import type { VisualLine } from './partial';

type FoldRow = Pick<Row, 'kind' | 'old' | 'new'>;

/** The scopes with a body below their head line: those a fold can hide something of. */
export function foldable(scopes: readonly Scope[]): Scope[] {
  return scopes.filter((s) => s.headLine < s.endLine);
}

/** A scope's name among a file's folds: its head line and title. */
export function scopeId(s: Pick<Scope, 'headLine' | 'title'>): string {
  return `${s.headLine}:${s.title}`;
}

/** A folded scope: the rows it hides, per block key, and what they hold. */
export interface ScopeFold {
  id: string;
  scope: Scope;
  /** Row indices hidden, per block key (only blocks it reaches). */
  rows: Map<string, Set<number>>;
  /** The first row hidden, where the fold's one line shows. */
  first: { block: string; row: number };
  /** New-side lines hidden. */
  lines: number;
  /** Added and removed rows hidden. */
  added: number;
  removed: number;
}

/** A row's new-side line, if it shows one. */
const newLine = (r: FoldRow) => (r.kind !== 'del' ? r.new : null);

/** One file's rows in display order, with their new-side lines sorted for lookup: built once per file's blocks. */
export interface FoldIndex {
  flat: { block: string; row: number; r: FoldRow }[];
  /** Flat indices of the rows that show a new-side line, in order (new lines ascend with them). */
  shown: number[];
}

export function foldIndex(blocks: readonly { key: string; rows: readonly FoldRow[] }[]): FoldIndex {
  const flat: FoldIndex['flat'] = [];
  const shown: number[] = [];
  for (const b of blocks)
    b.rows.forEach((r, row) => {
      if (newLine(r) !== null) shown.push(flat.length);
      flat.push({ block: b.key, row, r });
    });
  return { flat, shown };
}

/** In `index.shown`, the first position whose new line is past `line`. */
function after(index: FoldIndex, line: number): number {
  let lo = 0;
  let hi = index.shown.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (newLine(index.flat[index.shown[mid]].r)! <= line) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * The folds `folded` names (by `scopeId`) among `scopes`, over one file's
 * rows (`foldIndex` of its blocks). A fold hides the rows after the head
 * line's row (removed rows right after it included) through the scope's
 * last line. A fold inside another folded scope adds nothing and is left
 * out; a name the scopes no longer hold is ignored.
 */
export function scopeFolds(index: FoldIndex, scopes: readonly Scope[], folded: ReadonlySet<string>): ScopeFold[] {
  if (!folded.size) return [];
  const { flat, shown } = index;
  const chosen = foldable(scopes)
    .filter((s) => folded.has(scopeId(s)))
    .sort((a, b) => a.headLine - b.headLine || b.endLine - a.endLine);
  const out: ScopeFold[] = [];
  for (const s of chosen) {
    const outer = out[out.length - 1];
    if (outer && s.headLine >= outer.scope.headLine && s.endLine <= outer.scope.endLine) continue;
    // From the row after the head line's (when shown), else the first row past it.
    const headAt = after(index, s.headLine) - 1;
    const headRow = headAt >= 0 && newLine(flat[shown[headAt]].r) === s.headLine ? shown[headAt] : -1;
    const next = after(index, s.headLine);
    const from = headRow >= 0 ? headRow + 1 : next < shown.length ? shown[next] : flat.length;
    const lastAt = after(index, s.endLine) - 1;
    const to = lastAt >= 0 ? shown[lastAt] : -1;
    if (from >= flat.length || to < from) continue;
    const rows = new Map<string, Set<number>>();
    let lines = 0;
    let added = 0;
    let removed = 0;
    for (let i = from; i <= to; i++) {
      const f = flat[i];
      let set = rows.get(f.block);
      if (!set) rows.set(f.block, (set = new Set()));
      set.add(f.row);
      if (newLine(f.r) !== null) lines++;
      if (f.r.kind === 'add') added++;
      if (f.r.kind === 'del') removed++;
    }
    out.push({ id: scopeId(s), scope: s, rows, first: { block: flat[from].block, row: flat[from].row }, lines, added, removed });
  }
  return out;
}

/** The displayed lines (indices into `lines`) whose every cell's row is in `rows`. */
export function hiddenLines(lines: readonly VisualLine[], rows: ReadonlySet<number>): Set<number> {
  const out = new Set<number>();
  lines.forEach((l, i) => {
    const cells = [l.left, l.right].filter((c) => c !== null);
    if (cells.length && cells.every((c) => rows.has(c.index))) out.add(i);
  });
  return out;
}

/** The innermost foldable scope around `line` (its leading comments and head line included), or null. */
export function scopeUnder(scopes: readonly Scope[], line: number): Scope | null {
  let best: Scope | null = null;
  for (const s of foldable(scopes)) if (s.startLine <= line && line <= s.endLine && (!best || s.depth > best.depth)) best = s;
  return best;
}

/** The foldable scopes at nesting `depth`. */
export function levelScopes(scopes: readonly Scope[], depth: number): Scope[] {
  return foldable(scopes).filter((s) => s.depth === depth);
}

/** Lines `start`…`end` (1-based, inclusive) of one side. */
export interface LineSpan {
  start: number;
  end: number;
}

/** The lines a block's rows show on `side`, or null when it shows none there. */
export function sideRange(rows: readonly FoldRow[], side: 'LEFT' | 'RIGHT'): LineSpan | null {
  let start = Infinity;
  let end = -Infinity;
  for (const r of rows) {
    const n = side === 'LEFT' ? (r.kind !== 'add' ? r.old : null) : newLine(r);
    if (n === null) continue;
    start = Math.min(start, n);
    end = Math.max(end, n);
  }
  return start <= end ? { start, end } : null;
}

/**
 * The next larger selection around `sel`: the change `block`'s lines (on
 * the selection's side), then each scope around it (leading comments
 * included), whichever is smallest of those strictly larger. Null when
 * nothing encloses it.
 */
export function expandSelection(sel: LineSpan, scopes: readonly Scope[], block: LineSpan | null): LineSpan | null {
  const holds = (r: LineSpan) => r.start <= sel.start && sel.end <= r.end && r.end - r.start > sel.end - sel.start;
  const candidates: LineSpan[] = scopes.map((s) => ({ start: s.startLine, end: s.endLine }));
  if (block) candidates.push(block);
  let best: LineSpan | null = null;
  for (const c of candidates) if (holds(c) && (!best || c.end - c.start < best.end - best.start)) best = { start: c.start, end: c.end };
  return best;
}
